﻿<script>
	import { registerPageComplete } from "../../tealium";
    import model from "../../stores/modelStore";
	import textStore from "../../stores/textStore";
    import { projectSettings, acceptedFileUploadList } from "../../config";
    import { isValid } from "../../validation";

    import PageSection from "../../components/PageSection.svelte";
    import Form from "../../components/Form.svelte";
    import FormRow from "../../components/FormRow.svelte";
    import FormColumn from "../../components/FormColumn.svelte";
    import YesNoGroup from "../../components/YesNoGroup.svelte";
    import TextInput from "../../components/TextInput.svelte";
    import TextArea from "../../components/TextArea.svelte";
    import Select from "../../components/Select.svelte";
    import RadioGroup from "../../components/RadioGroup.svelte";
    import RadioGroupWithImage from "../../components/RadioGroupWithImage.svelte";
    import MonthYearSelect from "../../components/MonthYearSelect.svelte";
    import ClassActionEmailAddress from "../../components/ClassActionEmailAddress.svelte";
    import ClassActionPhoneNumber from "../../components/ClassActionPhoneNumber.svelte";
    import NavigationButtons from "../../components/NavigationButtons.svelte";
    import Deceased from "../../components/Deceased.svelte";
    import FileUpload from "../../components/FileUpload.svelte";
    import EmploymentPlaces from "../../components/EmploymentPlaces.svelte";
    import Sibling from "../../components/Sibling.svelte";
    import CheckboxListDelimiter from "../../components/CheckboxListDelimiter.svelte";

    export let pageName = null;
    export let pageTitle = null;
    export let lastVisitedPage = null;
	export let persistUrl = null;
    export let disabled = null;
    export let required = null;
    
    const filePrefix = "DOB";
    const workDocumentFilePrefix = "WORK";

	let buttons;

    export const relationshipOptions = [
        { label: "Spouse (husband/wife/partner – married or defacto)", value: "Spouse" },
        { label: "Child", value: "Child" },
    ];

    const knowAboutOptions = [
        { label: "The deceased person told me", value: "The deceased person told me" },
        { label: "Someone who knew the deceased person told me", value: "Someone who knew the deceased person told me" },
        { label: "I have seen records", value: "I have seen records" },
        { label: "Other", value: "Other" },
    ];

    if (!disabled) {
        $model.lastPageVisited = lastVisitedPage;
        document.title = pageTitle;
    }

    const handleSubmit = async () => {
		buttons.blockNext();
        
        // clean up
        cleanSelections();

		let errors = isValid();

		if (!errors.length) {
			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
				console.log(response);
			} else {
				registerPageComplete(
					pageName,
					$model.contact.emailAddress,
					$model.contact.phoneNumber,
				);
				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};

	const cleanSelections = () => {
	};

    if (!$model.deceased){
        $model.deceased = {};
    }


    //$:{
    //    console.log("Spouse or Child Claimants", $model);
    //}
</script>

<PageSection>
	<Form id="form-spouseChildClaimants-details" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Spouse or Child Claimants</h1>
			</FormColumn>
		</FormRow>
        <FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<p>Only complete this section if you are a descendant claimant. That means you are either a spouse (husband/wife/partner - married or defacto) or child of an Aboriginal or Torres Strait Islander person who worked in Western Australia between 11 December 1936 to 9 June 1972 for no money or only a small amount of money who has passed away (referred to as the "deceased person"). If you are claiming for more than one deceased person, for example a spouse and one or both parents, fill in additional forms.</p>
			    <ul>
                    <li>
                        Spouse or defacto spouse means the most recent person to marry and/or be in a relationship for two or more
                        years with the deceased person, before their death.
                    </li>
                    <li>
                        A child means a natural child of the deceased person, or any child who was legally adopted or adopted under Aboriginal laws and customs.
                    </li>
                </ul>
            </FormColumn>
		</FormRow>

		<div class="confirmation-box-shadow">
            <FormRow>
                <FormColumn>
                    <Deceased
                        {required}
					    {disabled}
                        bind:firstName={$model.deceased.firstName}
                        bind:surname={$model.deceased.surname}
                        bind:dateOfBirth={$model.claimDetail.deceasedDOB}
                        bind:hasOtherNames={$model.claimDetail.hasOtherNames}
                        bind:otherNamesList={$model.deceased.otherNames}
                        bind:birthPlace={$model.claimDetail.deceasedBirthPlace}
                        bind:skyDiscoveryDeceasedDob={$model.claimDetail.skyDiscovery_DeceasedDOB}
                    >
                    </Deceased>
                </FormColumn>
            </FormRow>

            <FormRow>
			    <FormColumn>
				     <YesNoGroup
					    {disabled}
					    {required}
					    name="firstNations"
					    label="Was the deceased person Aboriginal or Torres Strait Islander?"
					    bind:value={$model.claimDetail.aborginalTorresStraitIslander}
					    ></YesNoGroup>
			    </FormColumn>
		    </FormRow>

            <FormRow>
                <FormColumn>
                    <YesNoGroup
                        {required}
			            {disabled}
                        bind:value={$model.claimDetail.haveDeceasedDOBDocuments}
                        name="dobkDocuments"
                        label="Do you have any documents confirming the identity or date of birth of the deceased person? (i.e. birth, death or marriage certificate)"
                    />
                </FormColumn>
            </FormRow>
            {#if $model.claimDetail.haveDeceasedDOBDocuments === "Yes"}
            <FormRow>
                <FormColumn>
                    <FileUpload
                        {disabled}
                        registrationToken={$model.registrationToken}
                        filenamePrefix="{filePrefix}"
					    label="Please upload copies of these documents. This is not compulsory but any documents that are available may assist with registering and paying the claimant compensation money."
                        bind:documentList={$model.deceasedDobDocumentList}>
                    </FileUpload>
                </FormColumn>
            </FormRow>
            {/if}

            <FormRow>
			    <FormColumn>
				    <RadioGroup
					    {disabled}
					    {required}
					    name="relationshipToDeceased"
					    label="What was {$textStore.yourTheir} relationship to the deceased?"
					    bind:value={$model.claimDetail.relationshipToDeceased}
					    optionList={relationshipOptions}
					    on:change></RadioGroup>
			    </FormColumn>
		    </FormRow>

            <FormRow>
                <FormColumn>
                    <Sibling
                        required={false}
			            {disabled}
                        bind:siblingDetails={$model.claimDetail.siblingDetails}
                        bind:claimDetail={$model.claimDetail}
                        useDeceasedLabels={true}
                        />
                </FormColumn>
            </FormRow>
            <FormRow>
                <FormColumn>
                    <YesNoGroup
                        {required}
			            {disabled}
                        bind:value={$model.claimDetail.workedDuringPeriod}
                        name="workedDuringPeriod"
                        label="Did the deceased person work in Western Australia between 11 December 1936 and 9 June 1972?"
                    />
                </FormColumn>
            </FormRow>

            <EmploymentPlaces
                {required}
			    {disabled}
                bind:employmentDetails={$model.claimDetail.employmentDetails}
                label="Please provide details below of AT LEAST ONE PLACE the deceased person worked between 11 December 1936 and 9 June 1972 for no money or only a small amount of money. For example, on a station or at a mission that you can name below."
            >
            </EmploymentPlaces>

            <FormRow>
                <FormColumn>
                    <YesNoGroup
                        {required}
			            {disabled}
                        bind:value={$model.claimDetail.paidAnyMoney}
                        name="paidAnyMoney"
                        label="Was the deceased person paid no money or only a small amount of money for all or some of this work?"
                    />
                </FormColumn>
            </FormRow>

            {#if $model.claimDetail.paidAnyMoney==="Yes"}
                <FormRow>
                    <FormColumn>
                        {#if $model.skyDiscoveryId != null && disabled}                        
                            <CheckboxListDelimiter
                                label="label"
                                bind:delimitedString={$model.claimDetail.knowAboutPaidAnyMoney}
                                optionsList="{knowAboutOptions}"
                                {disabled}
                            ></CheckboxListDelimiter>
                        {:else}
                            <Select {required} {disabled} 
                            label="How do you know this?" 
                            id="knowAboutPaidAnyMoney" 
                            bind:value="{$model.claimDetail.knowAboutPaidAnyMoney}" 
                            optionList="{knowAboutOptions}" 
                            textProperty="label" 
                            valueProperty="value"></Select>
                        {/if}
                    </FormColumn>
                </FormRow>

                {#if $model.claimDetail.knowAboutPaidAnyMoney != null && $model.claimDetail.knowAboutPaidAnyMoney.includes("Other")}
                <FormRow>
                    <FormColumn>
                        <TextInput label="Please specify" {required} {disabled} 
                            bind:value="{$model.claimDetail.knowAboutPaidAnyMoneyOther}" id="knowAboutPaidAnyMoney-other" />
                    </FormColumn>
                </FormRow>
                {/if}
            {/if}

            <FormRow>
                <FormColumn>
                    <YesNoGroup
                        {required}
			            {disabled}
                        bind:value={$model.claimDetail.haveWorkDocuments}
                        name="haveWorkDocuments"
                        label="Do you have any records about the deceased persons' work between 11 December 1936 and 9 June 1972, or your relationship with the deceased?"
                    />
                </FormColumn>
            </FormRow>

            {#if $model.claimDetail.haveWorkDocuments === "Yes"}
            <FormRow>
                <FormColumn>
                    {#if (!disabled)}
                        <p>
                            Please upload copies of these documents. 
                            This is not compulsory but any documents that are available may assist with showing eligibility of the deceased person.
                        </p>
                    {/if}
                    <FileUpload
                        {disabled}
                        registrationToken={$model.registrationToken}
                        filenamePrefix="{workDocumentFilePrefix}"
                        bind:documentList={$model.deceasedWorkDocumentList}>
                    </FileUpload>
                </FormColumn>
            </FormRow>
            {/if}

            <FormRow>
                <FormColumn>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label>The WA Government may have records about the deceased person's work history that could assist your claim.</label>
                </FormColumn>
            </FormRow>

            <FormRow>
                <FormColumn>
                    <YesNoGroup
                        {required}
			            {disabled}
                        bind:value={$model.claimDetail.consentShineProvideInfoToGov}
                        name="consentShineProvideInfoToGov"
                        label="Do you consent to Shine Lawyers providing the information in the Registration Form (including your personal details) to the WA Government to request records about the deceased person?"
                    />
                    <p>
                        This is not compulsory but any documents that are available may assist with registering and showing eligibility of the deceased person.
                    </p>
                    <p>
                        Even if you tick "no", the State may be given some information about you and the deceased person during the administration process, including your and their names and dates of birth.
                    </p>
                </FormColumn>
            </FormRow>
            
        </div>

	    <FormRow class="confirmation-hide">
			<FormColumn>
				<NavigationButtons bind:buttons
					nextPageLabel={$model.confirmationPage ? "Confirmation" : "Next"} />
			</FormColumn>
		</FormRow>
    </Form>
</PageSection>

<style>
    h1 {
        margin-bottom: 20px;
    }
</style>